import { createGlobalStyle } from 'styled-components';

export const GlobalStyles = createGlobalStyle`
  /* Inter font is preloaded in index.html for faster loading and to prevent FOUC */

  :root {
    --bg-primary: #0D1117; /* Cosmic Abyss */
    --bg-secondary: #161B22; /* Card/Header/Sidebar */
    --text-primary: #E2E8F0;
    --text-secondary: #94A3B8;
    --accent-cyan: #22D3EE;
    --accent-cyan-dim: rgba(34, 211, 238, 0.1);
    --border-color: #1f2a37;
    --hover-bg: #1f2a37;
  }

  body {
    margin: 0;
    padding: 0;
    font-family: 'Inter', sans-serif;
    background-color: var(--bg-primary);
    color: var(--text-primary);
    overflow-x: hidden;
  }

  /* Custom Scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: var(--bg-secondary);
  }

  ::-webkit-scrollbar-thumb {
    background: #2D3748;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #4A5568;
  }

  /* Overrides for Ant Design */
  .ant-layout {
    background: var(--bg-primary) !important;
  }

  .ant-layout-header {
    background: var(--bg-secondary) !important;
    border-bottom: 1px solid var(--border-color);
    padding: 0 16px; /* Responsive padding handled in component */
  }

  .ant-layout-sider {
    background: var(--bg-secondary) !important;
    border-right: 1px solid var(--border-color);
  }

  .ant-menu {
    background: var(--bg-secondary) !important;
    color: var(--text-secondary) !important;
    border-right: none !important;
  }

  .ant-menu-item {
    color: var(--text-secondary) !important;
    border-radius: 0.75rem !important; /* rounded-xl */
    margin: 4px 8px !important;
    width: calc(100% - 16px) !important;
    
    &:hover {
      background-color: var(--hover-bg) !important;
      color: var(--accent-cyan) !important;
    }
  }

  .ant-menu-item-selected {
    background-color: var(--accent-cyan-dim) !important;
    color: var(--accent-cyan) !important;
    font-weight: 600;
  }

  .ant-menu-submenu-title {
    color: var(--text-secondary) !important;
    
    &:hover {
      color: var(--accent-cyan) !important;
    }
  }

  /* Card Styles */
  .ant-card {
    background-color: var(--bg-secondary) !important;
    border: 1px solid var(--border-color) !important;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2) !important;
    color: var(--text-primary) !important;
  }

  .ant-card-head {
    border-bottom: 1px solid var(--border-color) !important;
    color: var(--text-primary) !important;
  }

  .ant-card-head-title {
    color: var(--text-primary) !important;
  }

  /* Typography Overrides */
  h1, h2, h3, h4, h5, h6 {
    color: var(--text-primary) !important;
  }

  .ant-typography {
    color: var(--text-primary) !important;
  }

  /* Table Styles */
  .ant-table {
    background: transparent !important;
    color: var(--text-primary) !important;
  }

  .ant-table-thead > tr > th {
    background: var(--bg-secondary) !important;
    color: var(--text-secondary) !important;
    border-bottom: 1px solid var(--border-color) !important;
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid var(--border-color) !important;
    color: var(--text-primary) !important;
  }

  .ant-table-tbody > tr:hover > td {
    background: var(--hover-bg) !important;
  }

  /* Input Styles - Comprehensive Form Styling */
  .ant-input,
  .ant-input-password input,
  .ant-input-password,
  .ant-select-selector,
  .ant-select-selection-search-input,
  .ant-picker,
  .ant-picker-input > input,
  .ant-input-number,
  .ant-input-number-input,
  .ant-textarea,
  .ant-input-group-wrapper .ant-input,
  .ant-input-affix-wrapper {
    background-color: var(--bg-primary) !important;
    background: var(--bg-primary) !important;
    border-color: var(--border-color) !important;
    color: var(--text-primary) !important;
  }

  /* Remove white internal layers */
  .ant-input::placeholder,
  .ant-input-password input::placeholder,
  .ant-select-selection-placeholder,
  .ant-picker-input > input::placeholder {
    color: #6b7280 !important;
  }

  /* Autofill styles */
  .ant-input:-webkit-autofill,
  .ant-input:-webkit-autofill:hover,
  .ant-input:-webkit-autofill:focus,
  .ant-input-password input:-webkit-autofill,
  .ant-input-password input:-webkit-autofill:hover,
  .ant-input-password input:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 1000px var(--bg-primary) inset !important;
    -webkit-text-fill-color: var(--text-primary) !important;
    background-color: var(--bg-primary) !important;
    background: var(--bg-primary) !important;
    transition: background-color 5000s ease-in-out 0s;
  }

  /* Input focus and hover states */
  .ant-input:hover,
  .ant-input:focus,
  .ant-input-focused,
  .ant-input-password:hover,
  .ant-input-password:focus,
  .ant-input-password-focused,
  .ant-select:hover .ant-select-selector,
  .ant-select-focused .ant-select-selector,
  .ant-picker:hover,
  .ant-picker-focused {
    border-color: var(--accent-cyan) !important;
    box-shadow: 0 0 0 2px rgba(34, 211, 238, 0.1) !important;
  }

  /* Select dropdown */
  .ant-select-dropdown {
    background-color: var(--bg-secondary) !important;
    border: 1px solid var(--border-color) !important;
  }

  .ant-select-item {
    color: var(--text-primary) !important;
    background-color: var(--bg-secondary) !important;
  }

  .ant-select-item:hover,
  .ant-select-item-option-active {
    background-color: var(--hover-bg) !important;
  }

  .ant-select-item-option-selected {
    background-color: var(--accent-cyan-dim) !important;
    color: var(--accent-cyan) !important;
  }

  /* Ensure selected values are visible on dark backgrounds for selects using this helper class */
  .select-with-text .ant-select-selection-item,
  .select-with-text .ant-select-selection-item span,
  .select-with-text .ant-select-selection-placeholder {
    color: var(--text-primary) !important;
  }

  /* Input group */
  .ant-input-group-addon {
    background-color: var(--bg-secondary) !important;
    border-color: var(--border-color) !important;
    color: var(--text-primary) !important;
  }

  /* Input affix wrapper (prefix/suffix) */
  .ant-input-affix-wrapper {
    background-color: var(--bg-primary) !important;
  }

  .ant-input-affix-wrapper:hover,
  .ant-input-affix-wrapper-focused {
    border-color: var(--accent-cyan) !important;
  }

  .ant-input-affix-wrapper .ant-input {
    background: transparent !important;
  }

  /* Input number */
  .ant-input-number-handler-wrap {
    background-color: var(--bg-secondary) !important;
    border-left: 1px solid var(--border-color) !important;
  }

  .ant-input-number-handler {
    color: var(--text-secondary) !important;
  }

  .ant-input-number-handler:hover {
    color: var(--accent-cyan) !important;
  }

  /* Date picker */
  .ant-picker-dropdown {
    background-color: var(--bg-secondary) !important;
  }

  .ant-picker-panel {
    background-color: var(--bg-secondary) !important;
    border-color: var(--border-color) !important;
  }

  .ant-picker-header {
    border-bottom: 1px solid var(--border-color) !important;
  }

  .ant-picker-content th,
  .ant-picker-content td {
    color: var(--text-primary) !important;
  }

  .ant-picker-cell:hover:not(.ant-picker-cell-disabled) .ant-picker-cell-inner {
    background-color: var(--hover-bg) !important;
  }

  .ant-picker-cell-selected .ant-picker-cell-inner {
    background-color: var(--accent-cyan-dim) !important;
    color: var(--accent-cyan) !important;
  }

  /* Modal Styles */
  .ant-modal-content {
    background-color: var(--bg-secondary) !important;
    border: 1px solid var(--border-color);
  }

  .ant-modal-header {
    background-color: var(--bg-secondary) !important;
    border-bottom: 1px solid var(--border-color) !important;
  }

  .ant-modal-title {
    color: var(--text-primary) !important;
  }

  .ant-modal-close {
    color: var(--text-secondary) !important;
  }

  /* Form Label Styles */
  .ant-form-item-label > label {
    color: var(--text-primary) !important;
  }

  .ant-form-item-label > label.ant-form-item-required:not(.ant-form-item-required-mark-optional)::before {
    color: var(--accent-cyan) !important;
  }

  /* Form Help Text */
  .ant-form-item-explain,
  .ant-form-item-extra {
    color: var(--text-secondary) !important;
  }

  /* ============================================
     RESPONSIVE DESIGN SYSTEM
     ============================================ */

  /* Responsive Breakpoints */
  /* Mobile: < 768px */
  /* Tablet: 768px - 1024px */
  /* Desktop: > 1024px */

  /* Mobile-First Responsive Tables */
  @media (max-width: 768px) {
    /* Make tables horizontally scrollable on mobile */
    .ant-table-wrapper {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }

    .ant-table {
      min-width: 600px; /* Ensure table doesn't shrink too much */
    }

    /* Stack table cells on mobile for better readability */
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 8px 4px !important;
      font-size: 12px !important;
    }

    /* Responsive card padding */
    .ant-card-body {
      padding: 12px !important;
    }

    .ant-card-head {
      padding: 12px 16px !important;
    }

    /* Responsive typography */
    h1 {
      font-size: 24px !important;
    }

    h2 {
      font-size: 20px !important;
    }

    h3 {
      font-size: 18px !important;
    }

    /* Responsive spacing */
    .ant-space {
      gap: 8px !important;
    }

    /* Responsive form items */
    .ant-form-item {
      margin-bottom: 16px !important;
    }

    /* Responsive buttons */
    .ant-btn {
      padding: 4px 12px !important;
      font-size: 13px !important;
    }

    /* Responsive modals */
    .ant-modal {
      margin: 0 !important;
      max-width: 100% !important;
      top: 0 !important;
      padding-bottom: 0 !important;
    }

    .ant-modal-content {
      border-radius: 0 !important;
      min-height: 100vh;
    }

    /* Responsive drawer */
    .ant-drawer-content-wrapper {
      width: 100% !important;
      max-width: 100% !important;
    }

    /* Responsive descriptions */
    .ant-descriptions-item-label,
    .ant-descriptions-item-content {
      padding: 8px 4px !important;
      font-size: 13px !important;
    }

    /* Responsive tabs */
    .ant-tabs-tab {
      padding: 8px 12px !important;
      font-size: 13px !important;
    }

    /* Responsive pagination */
    .ant-pagination {
      margin: 16px 0 !important;
    }

    .ant-pagination-options {
      display: none !important; /* Hide page size selector on mobile */
    }

    /* Responsive input groups */
    .ant-input-group-wrapper {
      width: 100% !important;
    }

    /* Responsive select dropdowns */
    .ant-select {
      width: 100% !important;
    }

    /* Responsive badges and tags */
    .ant-tag {
      font-size: 11px !important;
      padding: 2px 6px !important;
      margin: 2px !important;
    }

    /* Responsive avatars */
    .ant-avatar {
      width: 24px !important;
      height: 24px !important;
    }

    /* Responsive tooltips */
    .ant-tooltip {
      max-width: 90vw !important;
    }
  }

  /* Tablet adjustments */
  @media (min-width: 769px) and (max-width: 1024px) {
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 10px 8px !important;
      font-size: 13px !important;
    }

    .ant-card-body {
      padding: 16px !important;
    }
  }

  /* Utility classes for responsive containers */
  .mobile-stack {
    @media (max-width: 768px) {
      flex-direction: column !important;
    }
  }

  .mobile-full-width {
    @media (max-width: 768px) {
      width: 100% !important;
    }
  }

  .mobile-hide {
    @media (max-width: 768px) {
      display: none !important;
    }
  }

  .mobile-show {
    @media (min-width: 769px) {
      display: none !important;
    }
  }

  /* Responsive grid layouts */
  .responsive-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 16px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 12px;
    }
  }

  /* Responsive flex containers */
  .responsive-flex {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 12px;
    }
  }

  /* Scrollable container for mobile tables */
  .table-scroll-container {
    @media (max-width: 768px) {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      margin: 0 -16px;
      padding: 0 16px;
    }
  }
`;

